import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { DashboardDataset, DashboardGraph } from 'src/app/model/dashboard';
import { AdminService } from 'src/app/service/admin.service';
import { SystemService } from 'src/app/service/system.service';
import html2canvas from 'html2canvas';
import * as domToImage from 'dom-to-image';

// Ordens fixas (portadas do chart-component v1) pra os gráficos de status.
const ORDER_SUBMISSIONS_BY_STATUS = ['active', 'pending', 'accepted', 'rejected', 'withdrawn'];
const ORDER_REVIEWS_BY_STATUS = ['invited', 'confirmed', 'declined', 'reminded', 'late', 'completed', 'draft'];

// Paleta categórica. NOTA: temização por tokens do chair UI é refinamento
// posterior — ngx-charts é SVG/CSS, então dá pra puxar a paleta dos tokens.
const PALETTE = [
  '#192a59', '#3f7cac', '#5fa8d3', '#88b04b',
  '#e0a458', '#c1666b', '#7d8597', '#a3b18a', '#d98c5f', '#6b9080',
];

/**
 * Gráfico de um item de Dashboard, renderizado com **ngx-charts** (substitui o
 * `chart-component` v1 baseado em Chartist/ng-chartist legados).
 *
 * Mantém o contrato e a CAMADA DE DADOS do v1: recebe um `DashboardGraph`, busca
 * via `getDatasetData(datasetName)` (formato Chartist `{labels, series}`) e
 * aplica as mesmas transformações de label (undefined/Others/desconhecido, país
 * ISO→nome, afiliação, ordenação por status, tradução). Só o RENDER e o FORMATO
 * mudam: `{labels, series}` → `[{name, value}]` do ngx-charts.
 */
@Component({
  selector: 'app-dashboard-chart',
  templateUrl: './dashboard-chart.component.html',
  styleUrls: ['./dashboard-chart.component.scss'],
})
export class DashboardChartComponent implements OnInit {
  @Input() graphPosition: DashboardGraph;

  /** Elemento do gráfico — usado pra capturar a imagem no download. */
  @ViewChild('chartEl') chartEl: ElementRef<HTMLElement>;

  // `<button mat-icon-button>` é um Component Material — sem `read: ElementRef`
  // o ViewChild devolveria a instância do MatButton (sem `getBoundingClientRect`).
  // Esse ref é o ANCHOR do `<app-popover>` do menu de download (Fase 2).
  @ViewChild('downloadBtn', { read: ElementRef })
  private downloadBtnRef: ElementRef<HTMLElement>;
  get downloadBtnEl(): HTMLElement { return this.downloadBtnRef?.nativeElement; }

  /** Tipo do gráfico — 'Pie' | 'Bar' | 'Line' (vem de `dataset.type`). */
  type: string;
  /** Dados no formato do ngx-charts. */
  data: any[] = [];
  /** Cores por nome de ponto (status) — vêm dos tokens via CSS vars. Vazio = usa
   *  a paleta categórica (`colorScheme`). */
  customColors: any[] = [];
  hasData = false;
  loading = true;
  legendTitle = '';
  readonly colorScheme = { domain: PALETTE };

  /** Chaves de status (na ordem dos dados) p/ buscar a cor-token de cada ponto. */
  private statusVarKeys: string[] | null = null;

  constructor(
    private adminService: AdminService,
    private translate: TranslateService,
    private systemService: SystemService,
  ) {}

  get dataset(): DashboardDataset {
    return this.graphPosition.dataset as DashboardDataset;
  }
  get datasetName(): string {
    return this.dataset.datasetName;
  }
  /** Settings da opção `customOption` do dataset (translateLabel, legendTitle…). */
  private get customOption(): any {
    return this.dataset.options?.find(o => o.name === 'customOption')?.settings ?? {};
  }

  ngOnInit(): void {
    this.type = this.dataset.type as string;
    this.legendTitle = this.translate.instant('dataset-options.legends.' + this.datasetName);
    this.loadData();
  }

  private loadData(): void {
    this.adminService.getDatasetData(this.datasetName).subscribe(
      res => {
        const raw = res?.[this.datasetName];
        if (!raw) { this.finish([], []); return; }
        this.transform(((raw.labels || []) as string[]).slice(), ((raw.series || []) as any[]).slice());
      },
      () => this.finish([], []),
    );
  }

  /** Pipeline de transformação portado do v1 (adaptado pro formato ngx-charts). */
  private transform(labels: string[], series: any[]): void {
    // 1. Labels especiais.
    labels = labels.map(l => {
      if (l === 'undefined') { return this.translate.instant('dataset-options.undefinedLabel'); }
      if (l === 'Others') { return this.translate.instant('dataset-options.othersLabel'); }
      if (l === '') { return this.translate.instant('dataset-options.unknownCountryLabel'); }
      return l;
    });

    // 2. Ordenação fixa por status (submissions/reviews).
    const order = this.datasetName === 'submissionsByStatus' ? ORDER_SUBMISSIONS_BY_STATUS
      : this.datasetName === 'reviewsByStatus' ? ORDER_REVIEWS_BY_STATUS : null;
    if (order) {
      const idx = labels.reduce((m, l, i) => { m[l] = i; return m; }, {} as Record<string, number>);
      const newOrder = order.map(l => l.toUpperCase()).filter(l => l in idx);
      series = newOrder.map(l => series[idx[l]]);
      labels = newOrder;
      // Chaves p/ a cor-token de cada status (o token de review usa 'notified'
      // pra 'invited', como no v1).
      this.statusVarKeys = newOrder.map(k => {
        const key = k.toLowerCase();
        return key === 'invited' ? 'notified' : key;
      });
    }

    // 3. País (ISO→nome) e afiliação são resolvidos antes do finalize.
    const isCountry = this.datasetName === 'submissionsByCountry' || this.datasetName === 'authorsByCountry';
    const isAffiliation = this.datasetName === 'submissionsByAffiliation' || this.datasetName === 'authorsByAffiliation';

    if (isCountry) {
      this.systemService.countries.subscribe(countries => {
        this.finalize(labels.map(l => countries.find(c => c.isoCode === l)?.name ?? l), series);
      });
    } else if (isAffiliation) {
      this.finalize(
        labels.map(l => l === 'no_affiliation'
          ? this.translate.instant('dataset-options.no_affiliationLabel') : l),
        series);
    } else {
      this.finalize(labels, series);
    }
  }

  /** Aplica `translateLabel` (se configurado) e emite os dados. */
  private finalize(labels: string[], series: any[]): void {
    const co = this.customOption;
    if (co?.translateLabel) {
      forkJoin(labels.map(l => this.translate.get(`${co.translateLabelPrepend ?? ''}${l}`)))
        .subscribe(translated => this.finish(translated as string[], series));
    } else {
      this.finish(labels, series);
    }
  }

  /** Converte pro formato ngx-charts e finaliza (loading/hasData/cores). */
  private finish(labels: string[], series: any[]): void {
    // Normaliza a série numa lista de números. Pie/Bar mandam `number[]` (plano);
    // séries temporais (Line) costumam vir ANINHADAS (`number[][]`, uma linha
    // dentro de um array) — daí o "sem dados" pós-migração. Também tolera valores
    // como objeto `{ value }`.
    const raw = Array.isArray(series?.[0]) ? series[0] : series;
    const values: number[] = (raw || []).map(toNumber);

    if (this.type === 'Line') {
      this.data = [{
        name: this.datasetName,
        series: labels.map((l, i) => ({ name: l, value: values[i] ?? 0 })),
      }];
    } else {
      this.data = labels.map((l, i) => ({ name: l, value: values[i] ?? 0 }));
    }
    this.buildStatusColors();
    this.hasData = values.some(v => (v ?? 0) > 0);
    this.loading = false;
  }

  /** Pra gráficos de status, mapeia cada ponto (pelo nome traduzido) à cor-token
   *  correspondente, via `[customColors]`. Outros datasets usam a paleta. */
  private buildStatusColors(): void {
    if (!this.statusVarKeys || this.type === 'Line') { this.customColors = []; return; }
    this.customColors = this.data.map((point, i) => ({
      name: point.name,
      value: this.statusColor(this.statusVarKeys[i]) || PALETTE[i % PALETTE.length],
    }));
  }

  /** Lê a cor de fill do status nos CSS vars (`--chart-status-<key>`), emitidos
   *  a partir dos tokens `$submission-status`/`$review-status`. */
  private statusColor(varKey: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(`--chart-status-${varKey}`).trim();
  }

  /** Baixa o gráfico como PNG (html2canvas) ou SVG (dom-to-image). Esconde o
   *  botão de download durante a captura pra ele não sair na imagem. */
  saveAs(type: 'png' | 'svg'): void {
    const el = this.chartEl?.nativeElement;
    if (!el) { return; }
    const btn = el.querySelector('.dashboard-chart__download') as HTMLElement;
    const prevDisplay = btn?.style.display;
    if (btn) { btn.style.display = 'none'; }
    const restore = () => { if (btn) { btn.style.display = prevDisplay ?? ''; } };
    const name = this.datasetName || 'chart';

    if (type === 'png') {
      html2canvas(el, { logging: false }).then(canvas => {
        restore();
        this.triggerDownload(canvas.toDataURL('image/png'), `${name}.png`);
      }).catch(() => restore());
    } else {
      domToImage.toSvg(el).then((dataUrl: string) => {
        restore();
        this.triggerDownload(dataUrl, `${name}.svg`);
      }).catch(() => restore());
    }
  }

  private triggerDownload(href: string, filename: string): void {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.click();
  }
}

/** Coage um valor de série a número, tolerando `{ value }` (formato Chartist). */
function toNumber(v: any): number {
  if (v == null) { return 0; }
  if (typeof v === 'object') { return Number(v.value ?? 0); }
  return Number(v) || 0;
}
